'use client';

import { useCallback, useEffect, useState } from 'react';
import Link from 'next/link';
import { Swords, Trophy } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';
import { track } from '@/lib/analytics';

// "Bitka nedelje" at the top of the feed (2026-09-05): the newest live battle, or the latest
// resolved one. Visitors vote A/B without an account (cookie voter, same as the polls); after
// the market resolves the battle the tally freezes and sits next to the real winner.

type Side = {
  userId: string | null; username: string; displayName: string; image: string | null;
  direction: string | null; entryPrice: number | null; stopLoss: number | null; takeProfit: number | null;
  tradeStatus: string | null; tradeId: string | null;
};
type Battle = {
  id: string; ticker: string; timeframe: string | null; status: 'ACTIVE' | 'RESOLVED';
  createdAt: string; resolvedAt: string | null; winnerId: string | null; resolution: string | null;
  a: Side; b: Side; votes: { a: number; b: number; mine: 'A' | 'B' | null };
};

const fmt = (n: number | null) => n == null ? '—' : n >= 1000 ? n.toFixed(0) : n >= 100 ? n.toFixed(2) : n.toFixed(4);

export default function FeaturedBattleCard() {
  const { t } = useLang();
  const [battle, setBattle] = useState<Battle | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await fetch('/api/battles/featured', { cache: 'no-store' });
      if (!res.ok) return;
      const d = await res.json();
      setBattle(d.battle ?? null);
    } catch { /* optional widget */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!battle) return null;

  const live = battle.status === 'ACTIVE';
  const total = battle.votes.a + battle.votes.b;
  const pct = (n: number) => (total ? Math.round((n / total) * 100) : 0);
  const winnerSide: 'A' | 'B' | null = !live && battle.winnerId
    ? battle.winnerId === battle.a.userId ? 'A' : battle.winnerId === battle.b.userId ? 'B' : null
    : null;

  const vote = async (side: 'A' | 'B') => {
    if (!live || busy) return;
    setBusy(true);
    try {
      const res = await fetch(`/api/battles/${battle.id}/vote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ side }),
      });
      if (res.ok) {
        const d = await res.json();
        setBattle((b) => b ? { ...b, votes: { a: d.a, b: d.b, mine: d.side } } : b);
        track('battle_vote', { battleId: battle.id, side });
      }
    } catch { /* ignore */ } finally { setBusy(false); }
  };

  const Trader = ({ s, side }: { s: Side; side: 'A' | 'B' }) => {
    const isLong = /LONG|BUY/i.test(s.direction || '');
    const mine = battle.votes.mine === side;
    const won = winnerSide === side;
    const share = pct(side === 'A' ? battle.votes.a : battle.votes.b);
    return (
      <div className="flex-1 min-w-0 rounded-xl p-3 flex flex-col gap-2" style={{ background: 'var(--bg-sunken)', border: `1px solid ${won ? 'var(--win)' : mine ? 'var(--accent)' : 'var(--border-subtle)'}` }}>
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-8 h-8 rounded-full overflow-hidden flex items-center justify-center text-xs font-bold shrink-0" style={{ background: 'var(--accent)', color: 'var(--bg-base)' }}>
            {s.image ? <img src={s.image} alt="" className="w-full h-full object-cover" /> : s.displayName[0]?.toUpperCase()}
          </div>
          <Link href={`/profile/${s.username}`} className="text-sm font-semibold truncate" style={{ color: 'var(--text-primary)' }}>{s.displayName}</Link>
          {won && <Trophy size={14} style={{ color: 'var(--win)' }} />}
        </div>
        <div className="flex items-center gap-2 text-[11px] font-bold">
          <span className={isLong ? 'badge-long rounded px-1.5 py-0.5' : 'badge-short rounded px-1.5 py-0.5'}>{isLong ? '▲ LONG' : '▼ SHORT'}</span>
          <span className="font-mono-nums" style={{ color: 'var(--text-muted)' }}>TP {fmt(s.takeProfit)} · SL {fmt(s.stopLoss)}</span>
        </div>
        <div className="h-1.5 rounded-full" style={{ background: 'var(--bg-card)' }}>
          <div className="h-1.5 rounded-full" style={{ width: `${share}%`, background: won ? 'var(--win)' : 'var(--accent)' }} />
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[11px] font-semibold" style={{ color: 'var(--text-muted)' }}>{share}% · {side === 'A' ? battle.votes.a : battle.votes.b}</span>
          {live ? (
            <button
              onClick={() => vote(side)} disabled={busy || mine}
              className="px-2.5 py-1 rounded-lg text-[11px] font-bold"
              style={mine ? { background: 'var(--accent-muted)', color: 'var(--accent)' } : { background: 'var(--accent)', color: 'var(--bg-base)' }}
            >
              {mine ? t('Your pick') : t('First to TP')}
            </button>
          ) : (
            <span className="text-[11px] font-bold" style={{ color: won ? 'var(--win)' : 'var(--text-muted)' }}>
              {won ? t('Winner') : s.tradeStatus === 'LOSS' ? 'SL' : s.tradeStatus === 'WIN' ? 'TP' : s.tradeStatus ?? ''}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <section className="rounded-2xl p-4 sm:p-5" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', boxShadow: '0 2px 12px rgba(28,43,38,0.06)' }} aria-label={t('Battle of the week')}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Swords size={16} style={{ color: 'var(--accent)' }} />
          <h2 className="text-base font-bold" style={{ color: 'var(--text-primary)' }}>{t('Battle of the week')}</h2>
          <span className="text-xs font-mono-nums font-bold px-2 py-0.5 rounded-full" style={{ background: 'var(--bg-sunken)', color: 'var(--text-secondary)' }}>{battle.ticker}{battle.timeframe ? ` · ${battle.timeframe}` : ''}</span>
        </div>
        <span className="text-[11px] font-extrabold" style={{ color: live ? 'var(--bullish)' : 'var(--text-muted)' }}>
          {live ? `● ${t('LIVE')}` : battle.resolution === 'DRAW' ? t('DRAW') : t('RESOLVED')}
        </span>
      </div>
      <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
        {live ? t('Two traders, opposite calls, same chart. Who reaches take-profit first? Vote — the market decides.') : t('The market has spoken. This is how the vote went.')}
      </p>
      <div className="flex gap-3">
        <Trader s={battle.a} side="A" />
        <Trader s={battle.b} side="B" />
      </div>
      <div className="mt-3 flex items-center justify-between">
        <span className="text-[11px]" style={{ color: 'var(--text-muted)' }}>{total} {t('votes')}</span>
        <Link href="/battles" className="text-xs font-semibold" style={{ color: 'var(--accent)' }}>{t('All battles')} →</Link>
      </div>
    </section>
  );
}
